import { Form, Formik } from 'formik';
import { LinkIcon } from 'lucide-react';

import { useGroups } from '@/react/portainer/environments/environment-groups/queries';
import { AlertRuleRow } from '@/react/portainer/observability/types';
import { useAlertRuleAttachments } from '@/react/portainer/observability/AlertsView/AlertRuleEdit/useAlertRuleAttachments';
import { useUpdateAlertRuleAttachments } from '@/react/portainer/observability/AlertsView/AlertRuleEdit/useUpdateAlertRuleAttachments';
import { notifySuccess } from '@/portainer/services/notifications';
import { AlertRuleGroupDataTable } from '@/react/portainer/observability/AlertsView/AlertRuleEdit/AlertRuleGroupDataTable';
import {
  EnvironmentGroupsPicker,
  FormValues,
} from '@/react/portainer/observability/AlertsView/AlertRuleEdit/EnvironmentGroupsPicker';

import { Alert } from '@@/Alert';
import { Widget } from '@@/Widget';
import { TextTip } from '@@/Tip/TextTip';
import { Option } from '@@/form-components/PortainerSelect';

interface Props {
  alertRule: AlertRuleRow;
}

export function AlertRuleAttachments({ alertRule }: Props) {
  const groupsQuery = useGroups();
  const attachmentsQuery = useAlertRuleAttachments({ id: alertRule.id });
  const updateAttachments = useUpdateAlertRuleAttachments(alertRule.id);

  if (groupsQuery.error || attachmentsQuery.error) {
    return (
      <Alert color="error" title="Error loading groups">
        {(groupsQuery.error as Error)?.message ?? 'Unknown error'}
      </Alert>
    );
  }

  const groupOptions: Array<Option<number>> = (groupsQuery.data ?? [])
    .filter((g) => !g.Name.toLowerCase().includes('unassigned'))
    .map((g) => ({ value: g.Id, label: g.Name }));
  const attachmentGroups = attachmentsQuery.data ?? [];

  // Stub: initial selection derived from rule id until API endpoint exists
  const initialValues: FormValues = {
    environmentGroups: attachmentGroups.map((a) => a.endpointGroupId),
  };

  return (
    <>
      <Widget.Title icon={LinkIcon} title="Attachments" />

      <div className="flex flex-col gap-4 px-5 pb-4 pt-4">
        <Formik<FormValues>
          initialValues={initialValues}
          onSubmit={handleApply}
          enableReinitialize
        >
          <Form className="form-horizontal">
            <EnvironmentGroupsPicker
              groupOptions={groupOptions}
              groupOptionsLoading={groupsQuery.isLoading}
            />
          </Form>
        </Formik>

        <div className="flex items-center">
          <TextTip color="blue" className="small">
            Environments are filtered to those belonging to the selected groups.
          </TextTip>
        </div>
      </div>

      <Widget.Body>
        {attachmentGroups.length === 0 &&
        !attachmentsQuery.isLoading &&
        !groupsQuery.isLoading ? (
          <TextTip color="orange" className="small">
            No environment groups attached to this alert rule
          </TextTip>
        ) : (
          <div className="flex flex-col gap-4">
            {attachmentGroups.map((attachmentGroup) => (
              <AlertRuleGroupDataTable
                key={attachmentGroup.endpointGroupId}
                attachmentGroup={attachmentGroup}
                alertRuleId={alertRule.id}
                groupSize={
                  groupsQuery.data?.find(
                    (g) => g.Id === attachmentGroup.endpointGroupId
                  )?.Total
                }
              />
            ))}
          </div>
        )}
      </Widget.Body>
    </>
  );

  // Stub: replace with real mutation when API endpoint is available
  async function handleApply(values: FormValues) {
    try {
      await updateAttachments.mutateAsync({
        endpointGroupIds: values.environmentGroups,
      });
      notifySuccess('Success', 'Alert rule environment groups updated');
    } catch {
      // Error handled globally
    }
  }
}
